/**
 * ImportantPostView — Read-only, single-post web view for non-admin team members.
 *
 * This is the ONLY web view a team member is allowed to see, and only when they
 * arrive via the "Important Info" post+email deep link (/team?post=<id>). It
 * shows just that one post (read-only: no tabs, no feed, no messages, no
 * replying/reacting) plus a prompt to download the mobile app for everything
 * else. Any other navigation routes them to the Download App screen.
 */
import React, { useEffect, useState } from 'react';
import { LogOut, Megaphone, Mic, PlayCircle, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DownloadAppContent from '../shared/DownloadAppContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export default function ImportantPostView({ user, postId, onSignOut }) {
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/comms/v3/posts/${encodeURIComponent(postId)}?viewer_id=${encodeURIComponent(user?.id || '')}`
        );
        if (!res.ok) throw new Error('unavailable');
        const data = await res.json();
        if (active) {
          setPost(data?.post || data);
          setStatus('ok');
        }
      } catch {
        if (active) setStatus('error');
      }
    })();
    return () => { active = false; };
  }, [postId, user?.id]);

  const author = post?.author || {};
  const mediaUrls = Array.isArray(post?.media_urls) ? post.media_urls : [];
  const poll = post?.poll || null;
  const video = post?.video || null;
  const hasVoiceMemo = Boolean(post?.voice_memo_id || post?.has_voice_memo);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#1e1f22] flex flex-col items-center px-4 py-8" data-testid="important-post-view">
      <div className="w-full max-w-xl">
        {/* Brand / context header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
            <Megaphone className="w-5 h-5" />
            <span className="font-semibold text-sm tracking-wide uppercase">Important Info</span>
          </div>
          {onSignOut && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSignOut}
              data-testid="important-post-sign-out-btn"
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 gap-1.5"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </Button>
          )}
        </div>

        {status === 'loading' && (
          <div className="bg-card rounded-2xl border border-slate-200 dark:border-white/10 p-8 text-center text-slate-400" data-testid="important-post-loading">
            Loading…
          </div>
        )}

        {status === 'error' && (
          <div className="bg-card rounded-2xl border border-slate-200 dark:border-white/10 p-6 text-center" data-testid="important-post-error">
            <p className="text-slate-700 dark:text-slate-200 font-medium mb-1">This post isn't available here</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Open the Worship OS app to view it.
            </p>
          </div>
        )}

        {status === 'ok' && post && (
          <div className="bg-card rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden" data-testid="important-post-card">
            {/* Author */}
            <div className="flex items-center gap-3 px-5 pt-5">
              {author.avatar_url || author.avatar ? (
                <img src={author.avatar_url || author.avatar} alt={author.name || 'Author'} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-semibold">
                  {(author.name || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">{author.name || 'Worship OS'}</p>
                <p className="text-xs text-slate-400">{formatWhen(post.created_at)}</p>
              </div>
            </div>

            {/* Content */}
            {post.content && (
              <p className="px-5 pt-4 text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed" data-testid="important-post-content">
                {post.content}
              </p>
            )}

            {/* Images */}
            {mediaUrls.length > 0 && (
              <div className="px-5 pt-4 space-y-3">
                {mediaUrls.map((url, i) => (
                  <img key={i} src={url} alt="Post attachment" className="w-full rounded-xl object-cover" />
                ))}
              </div>
            )}

            {/* GIF */}
            {post.gif_url && (
              <div className="px-5 pt-4">
                <img src={post.gif_url} alt="GIF" className="w-full rounded-xl" />
              </div>
            )}

            {/* Poll (read-only) */}
            {poll && (
              <div className="mx-5 mt-4 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-2 mb-3 text-slate-700 dark:text-slate-200 font-medium">
                  <BarChart3 className="w-4 h-4 text-indigo-500" />
                  {poll.question || 'Poll'}
                </div>
                <div className="space-y-2">
                  {(poll.options || []).map((opt, i) => {
                    const label = typeof opt === 'string' ? opt : (opt.text || opt.label || `Option ${i + 1}`);
                    const count = (opt && Array.isArray(opt.votes)) ? opt.votes.length : 0;
                    return (
                      <div key={i} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-card border border-slate-200 dark:border-white/10">
                        <span className="text-slate-700 dark:text-slate-200">{label}</span>
                        <span className="text-slate-400 text-xs">{count} {count === 1 ? 'vote' : 'votes'}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-3">Vote in the app</p>
              </div>
            )}

            {/* Video */}
            {video && (
              <div className="mx-5 mt-4 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center gap-3 text-slate-600 dark:text-slate-300">
                {video.thumbnail_url ? (
                  <img src={video.thumbnail_url} alt="Video" className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <PlayCircle className="w-6 h-6 text-indigo-500" />
                )}
                <span className="text-sm">Open the app to watch this video</span>
              </div>
            )}

            {/* Voice memo */}
            {hasVoiceMemo && (
              <div className="mx-5 mt-4 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center gap-3 text-slate-600 dark:text-slate-300">
                <Mic className="w-5 h-5 text-indigo-500" />
                <span className="text-sm">Open the app to listen to the voice memo</span>
              </div>
            )}

            <div className="h-5" />
          </div>
        )}

        {/* Download CTA — always shown so they're nudged back to mobile */}
        <div className="mt-8 bg-card rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm p-6">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-5">
            Reply, react, message your team and more — all in the app.
          </p>
          <DownloadAppContent variant="dialog" />
        </div>
      </div>
    </div>
  );
}
